/**
 * JobDetailView Component
 *
 * Detailed view of a scheduled job with execution history.
 * Uses Standard Component Library throughout.
 */

import React, { useState, useEffect } from 'react';
import {
  StandardButton,
  StandardCard,
  StandardLoadingState,
  StandardErrorState,
  StandardModal,
  useToast
} from '../../standard';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Play,
  Pause,
  AlertCircle,
  Database,
  FileText,
  Mail,
  BarChart3,
  Settings,
  Archive
} from 'lucide-react';
import { scheduledJobsStorage } from '../../../services/scheduledJobsStorage';
import * as scheduledJobsStorageRouter from '../../../services/scheduledJobsStorageRouter';
import { ScheduledJob, JobType } from '../../../types/scheduledJobs';
import { jobTypeDefinitions } from '../../../data/mockScheduledJobs';
import { JobExecutionHistory } from './JobExecutionHistory';

// Icon mapping for job types
const jobTypeIcons: Record<JobType, React.ComponentType<{ size?: number; className?: string }>> = {
  data_retention_purge: Database,
  scheduled_report: FileText,
  data_cleanup: Archive,
  reminder_email: Mail,
  analytics_aggregation: BarChart3,
  custom: Settings
};

interface JobDetailViewProps {
  jobId: string;
  onBack: () => void;
  onEdit: (jobId: string) => void;
}

export const JobDetailView: React.FC<JobDetailViewProps> = ({
  jobId,
  onBack,
  onEdit
}) => {
  const { success, error: showError } = useToast();

  const [job, setJob] = useState<ScheduledJob | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    scheduledJobsStorageRouter.getJobAsync(jobId).then(loadedJob => {
      if (cancelled) return;
      setJob(loadedJob ?? null);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [jobId]);

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!jobId) {
      setIsDeleteModalOpen(false);
      return;
    }
    const deleted = await scheduledJobsStorageRouter.deleteJobAsync(jobId);
    if (deleted && job) {
      success('Job Deleted', `${job.name} has been deleted`);
      onBack();
    } else {
      showError('Error', 'Failed to delete job');
    }
    setIsDeleteModalOpen(false);
  };

  const handleToggleStatus = async () => {
    if (!jobId) return;
    const updated = await scheduledJobsStorageRouter.toggleJobStatusAsync(jobId);
    if (updated) {
      setJob(updated);
      success(updated.isActive ? 'Job Activated' : 'Job Paused', `${updated.name} has been ${updated.isActive ? 'activated' : 'paused'}`);
    }
  };

  // Handle run job
  const handleRunJob = async () => {
    if (!job) return;

    setIsRunning(true);
    try {
      success('Job Started', `${job.name} is now running`);

      // Simulate job execution
      setTimeout(() => {
        success('Job Completed', `${job.name} completed successfully`);
        setIsRunning(false);
      }, 2000);
    } catch (err) {
      showError('Error', 'Failed to run job');
      setIsRunning(false);
    }
  };

  if (isLoading) {
    return <StandardLoadingState message="Loading job details..." />;
  }

  if (!job) {
    return (
      <StandardErrorState
        title="Job Not Found"
        message="The requested job could not be found"
        onRetry={onBack}
        retryLabel="Back to Jobs"
      />
    );
  }

  const jobTypeDef = jobTypeDefinitions.find(def => def.type === job.type);
  const JobTypeIcon = jobTypeIcons[job.type];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <StandardButton
            variant="ghost"
            leftIcon={<ArrowLeft size={16} />}
            onClick={onBack}
          >
            Back
          </StandardButton>

          <div>
            <div className="flex items-center gap-3">
              {JobTypeIcon && <JobTypeIcon size={24} className="text-gray-400" />}
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {job.name}
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {job.description}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <StandardButton
            variant="secondary"
            leftIcon={<Play size={16} />}
            onClick={handleRunJob}
            isLoading={isRunning}
          >
            Run Now
          </StandardButton>
          <StandardButton
            variant="secondary"
            leftIcon={job.isActive ? <Pause size={16} /> : <Play size={16} />}
            onClick={handleToggleStatus}
          >
            {job.isActive ? 'Pause' : 'Activate'}
          </StandardButton>
          <StandardButton
            variant="secondary"
            leftIcon={<Edit size={16} />}
            onClick={() => onEdit(jobId)}
          >
            Edit
          </StandardButton>
          <StandardButton
            variant="danger"
            leftIcon={<Trash2 size={16} />}
            onClick={handleDelete}
          >
            Delete
          </StandardButton>
        </div>
      </div>

      {/* Job Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <StandardCard>
            <StandardCard.Header title="Basic Information" />
            <StandardCard.Body>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Job Type
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {jobTypeDef?.label}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Status
                  </dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      job.isActive
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                    }`}>
                      {job.isActive ? 'Active' : 'Paused'}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Created
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {new Date(job.createdDate).toLocaleDateString()}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Created By
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {job.createdBy}
                  </dd>
                </div>
              </dl>
            </StandardCard.Body>
          </StandardCard>

          {/* Schedule */}
          <StandardCard>
            <StandardCard.Header title="Schedule" />
            <StandardCard.Body>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Frequency
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100 capitalize">
                    {job.schedule.frequency}
                  </dd>
                </div>

                {job.schedule.time && (
                  <div>
                    <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Time
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {job.schedule.time}
                    </dd>
                  </div>
                )}

                <div>
                  <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Timezone
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                    {job.schedule.timezone || 'UTC'}
                  </dd>
                </div>

                {job.nextRun && (
                  <div>
                    <dt className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Next Run
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                      {new Date(job.nextRun).toLocaleString()}
                    </dd>
                  </div>
                )}
              </dl>
            </StandardCard.Body>
          </StandardCard>

          {/* Configuration */}
          {Object.keys(job.configuration).length > 0 && (
            <StandardCard>
              <StandardCard.Header title="Configuration" />
              <StandardCard.Body>
                <dl className="space-y-3">
                  {Object.entries(job.configuration).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <dt className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </dt>
                      <dd className="text-sm text-gray-900 dark:text-gray-100">
                        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </StandardCard.Body>
            </StandardCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <StandardCard>
            <StandardCard.Header title="Quick Stats" />
            <StandardCard.Body>
              <div className="space-y-4">
                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {job.lastRun ? new Date(job.lastRun).toLocaleDateString() : 'Never'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Last Run
                  </div>
                </div>

                <div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {job.nextRun ? new Date(job.nextRun).toLocaleDateString() : 'Not Scheduled'}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Next Run
                  </div>
                </div>
              </div>
            </StandardCard.Body>
          </StandardCard>

          {/* Notifications */}
          <StandardCard>
            <StandardCard.Header title="Notifications" />
            <StandardCard.Body>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">On Success</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {job.notifications.onSuccess ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">On Failure</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {job.notifications.onFailure ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {job.notifications.recipients.length > 0 && (
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-gray-700 dark:text-gray-300 mb-1">Recipients:</div>
                    <div className="text-gray-600 dark:text-gray-400 text-xs space-y-1">
                      {job.notifications.recipients.map((recipient, index) => (
                        <div key={index}>{recipient}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </StandardCard.Body>
          </StandardCard>

          {/* Retry Policy */}
          <StandardCard>
            <StandardCard.Header title="Retry Policy" />
            <StandardCard.Body>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 dark:text-gray-300">Enabled</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {job.retryPolicy.enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                {job.retryPolicy.enabled && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">Max Attempts</span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {job.retryPolicy.maxAttempts}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700 dark:text-gray-300">Retry Delay</span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {job.retryPolicy.retryDelayMs / 60000} min
                      </span>
                    </div>
                  </>
                )}
              </div>
            </StandardCard.Body>
          </StandardCard>
        </div>
      </div>

      {/* Execution History */}
      <JobExecutionHistory jobId={job.id} jobName={job.name} />

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Scheduled Job"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Are you sure you want to delete "{job.name}"?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone. The job will be permanently removed and will no longer execute.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={confirmDelete}
          >
            Delete Job
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
};

export default JobDetailView;
